import React, { useState, useEffect } from 'react';
import { Form, Input, Select, Button, Typography, Divider, Tag, Space } from 'antd';
import { PlusOutlined, CloseOutlined } from '@ant-design/icons';

const { Text } = Typography;
const { TextArea } = Input;

const RemoveColumnNodeProperties = ({ config, form, inputSchema }) => {
  const [columnsToRemove, setColumnsToRemove] = useState(config.columnsToRemove || []);
  const [newColumnName, setNewColumnName] = useState('');

  // Update form when columnsToRemove change
  useEffect(() => {
    form.setFieldsValue({
      columnsToRemove: columnsToRemove
    });
  }, [columnsToRemove, form]);

  // Add column to remove list
  const addColumnToRemove = () => {
    if (newColumnName.trim() && !columnsToRemove.includes(newColumnName.trim())) {
      setColumnsToRemove([...columnsToRemove, newColumnName.trim()]);
      setNewColumnName('');
    }
  };

  // Remove column from remove list
  const removeFromList = (columnName) => {
    setColumnsToRemove(columnsToRemove.filter(col => col !== columnName));
  };

  // Handle Enter key in input
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addColumnToRemove();
    }
  };

  const commonColumnsToRemove = [
    'id', 'created_at', 'updated_at', 'temp_column', 
    'internal_id', 'system_flag', 'debug_info'
  ];

  return (
    <div>
      <Form.Item label="Description" name="description" initialValue={config.description || 'Remove unnecessary columns from dataset'}>
        <TextArea rows={2} placeholder="Describe which columns will be removed and why" />
      </Form.Item>

      <Divider orientation="left">Columns to Remove</Divider>
      
      {/* Hidden form field to store columnsToRemove in form */}
      <Form.Item name="columnsToRemove" initialValue={columnsToRemove} style={{ display: 'none' }}>
        <Input />
      </Form.Item>

      {/* Add Column Input */}
      <div style={{ marginBottom: 16 }}>
        <Text style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: 4 }}>
          Column Name to Remove
        </Text>
        {inputSchema && inputSchema.length > 0 ? (
          <Select
            mode="multiple"
            style={{ width: '100%' }}
            placeholder="Select columns to remove"
            value={columnsToRemove}
            onChange={setColumnsToRemove}
            showSearch
          >
            {inputSchema.map(col => (
              <Select.Option key={col.name} value={col.name}>{col.name}</Select.Option>
            ))}
          </Select>
        ) : (
          <Space.Compact style={{ width: '100%' }}>
            <Input
              size="small"
              value={newColumnName}
              onChange={(e) => setNewColumnName(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Enter column name"
              style={{ flex: 1 }}
            />
            <Button 
              size="small"
              type="primary" 
              icon={<PlusOutlined />}
              onClick={addColumnToRemove}
              disabled={!newColumnName.trim() || columnsToRemove.includes(newColumnName.trim())}
            >
              Add
            </Button>
          </Space.Compact>
        )}
      </div>

      {/* Quick Add Common Columns - Only show if manual input is used or as helper */}
      {(!inputSchema || inputSchema.length === 0) && (
        <div style={{ marginBottom: 16 }}>
          <Text style={{ fontSize: '11px', color: '#999', display: 'block', marginBottom: 4 }}>
            Quick Add Common Columns:
          </Text>
          <Space wrap>
            {commonColumnsToRemove.map(colName => (
              <Button
                key={colName}
                size="small"
                type="dashed"
                style={{ fontSize: '10px', height: '24px' }}
                onClick={() => {
                  if (!columnsToRemove.includes(colName)) {
                    setColumnsToRemove([...columnsToRemove, colName]);
                  }
                }}
                disabled={columnsToRemove.includes(colName)}
              >
                {colName}
              </Button>
            ))}
          </Space>
        </div>
      )}

      {/* Selected Columns List - Only show if manual input is used (Select handles display otherwise) */}
      {(!inputSchema || inputSchema.length === 0) && columnsToRemove.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <Text style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: 8 }}>
            Selected Columns to Remove ({columnsToRemove.length}):
          </Text>
          <div style={{ 
            maxHeight: '120px', 
            overflowY: 'auto', 
            border: '1px solid #f0f0f0', 
            borderRadius: '4px', 
            padding: '8px' 
          }}>
            <Space wrap>
              {columnsToRemove.map(columnName => (
                <Tag
                  key={columnName}
                  closable
                  onClose={() => removeFromList(columnName)}
                  color="red"
                  style={{ marginBottom: 4 }}
                >
                  {columnName}
                </Tag>
              ))}
            </Space>
          </div>
        </div>
      )}

      {/* Options */}
      <Form.Item 
        label="Remove Strategy" 
        name="removeStrategy" 
        initialValue={config.removeStrategy || 'ignore_missing'}
      >
        <Select size="small">
          <Select.Option value="ignore_missing">Ignore if column doesn't exist</Select.Option>
          <Select.Option value="error_missing">Error if column doesn't exist</Select.Option>
          <Select.Option value="warn_missing">Warn if column doesn't exist</Select.Option>
        </Select>
      </Form.Item>

      <Divider orientation="left" style={{ fontSize: '12px' }}>Execution Behavior</Divider>
      
      <div style={{ padding: '8px 12px', backgroundColor: '#f9f9f9', borderRadius: '4px', fontSize: '12px' }}>
        <Text type="secondary">
          <strong>Remove Columns Behavior:</strong><br/>
          • Specified columns will be removed from the dataset<br/>
          • Original dataset structure is preserved for other columns<br/>
          • Missing columns handled based on strategy setting<br/>
          • Remaining data flows to next node
        </Text>
      </div>
    </div>
  );
};

export default RemoveColumnNodeProperties;